import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, LogIn, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const redirectTo = location.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) {
      toast.success("Signed in");
      navigate(redirectTo, { replace: true });
    } else {
      setError(res.error || "Sign in failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <div className="flex-1">
        <div className="max-w-md mx-auto px-6 md:px-8 py-14 md:py-20">
          <div className="cre-overline text-sky-700">Account</div>
          <h1 className="font-heading text-3xl font-black tracking-tight mt-1 text-slate-900">
            Sign in to save history
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Your saved analyses are kept for 1 month so you can revisit the
            reasoning trail, download the PDF, or re-share the read-only link.
          </p>

          <form
            onSubmit={submit}
            className="cre-card p-6 mt-8 space-y-4"
            data-testid="login-form"
          >
            <div>
              <Label className="cre-overline">Email</Label>
              <Input
                type="email"
                autoComplete="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-sky-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="cre-overline">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password-link"
                  className="text-[11px] font-medium text-sky-700 hover:text-sky-600 underline-offset-4 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 bg-white border-slate-200 focus-visible:ring-sky-500 pr-10"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" strokeWidth={2} />
                  ) : (
                    <Eye className="h-4 w-4" strokeWidth={2} />
                  )}
                </button>
              </div>
            </div>
            {error ? (
              <div
                data-testid="login-error"
                className="text-xs text-rose-700 border border-rose-200 bg-rose-50 rounded-md px-3 py-2"
              >
                {error}
              </div>
            ) : null}
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full cre-cta-primary rounded-lg font-semibold h-11"
            >
              <LogIn className="h-4 w-4 mr-2" strokeWidth={2} />
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-center text-sm text-slate-600">
              New to CRE?{" "}
              <Link
                to="/register"
                data-testid="login-goto-register"
                className="text-sky-700 hover:text-sky-600 underline-offset-4 hover:underline font-semibold"
              >
                Create an account
              </Link>
            </div>
            <div className="text-center pt-2 border-t border-slate-100">
              <Link
                to="/preview"
                data-testid="login-try-preview"
                className="text-[13px] font-semibold text-sky-700 hover:text-sky-800 underline-offset-4 hover:underline inline-flex items-center gap-1.5"
              >
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                Or try CRE without login →
              </Link>
            </div>
          </form>

          <div
            className="mt-4 flex items-start gap-2 text-[12px] text-slate-500"
            data-testid="login-signin-required-note"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 mt-0.5" strokeWidth={2} />
            <p>
              Sign in is required to create incidents, dispatch agents, save
              history, and manage your plan.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
